import React, { useContext, useState, useEffect } from "react";
import ThemeContext from "../../lib/theme-context";
import AuthContext from "../../lib/auth-context";
import { getAccountById, getSelectedAccountId } from "../../utils/accountUtils";
import { getCurrencyForSelectedAccount } from "../../utils/currencyUtils";
import Tooltip from "@mui/material/Tooltip";

const StatsBar = ({ 
  calculateFields, 
  showBacktestTrades, 
  selectedAccountId: propSelectedAccountId,
  getFilteredTradesByAccount,
  realisedPercentage,
  realisedPercentageWithFees,
  realisedPercentageBacktest,
  realisedPercentageWithFeesBacktest,
  realisedReturn,
  realisedReturnGross,
  realisedReturnBacktest,
  realisedReturnGrossBacktest
}) => {
  const theme = useContext(ThemeContext);
  const auth = useContext(AuthContext);
  const [selectedAccount, setSelectedAccount] = React.useState(null);
  const [showBalance, setShowBalance] = React.useState(() => localStorage.getItem("show-balance-statsbar") === "true");
  const [clickedButton, setClickedButton] = React.useState(null);
  const lastClickTime = React.useRef(0);
  const [showGross, setShowGross] = useState(() => localStorage.getItem("show-gross-statsbar") === "true");

  // Listen for gross-net-statsbar-changed events from profile dropdown
  useEffect(() => {
    const handleGrossNetStatsBarChanged = (event) => {
      if (event.detail && event.detail.showGross !== undefined) {
        setShowGross(event.detail.showGross);
      }
    };

    window.addEventListener("gross-net-statsbar-changed", handleGrossNetStatsBarChanged);

    return () => {
      window.removeEventListener("gross-net-statsbar-changed", handleGrossNetStatsBarChanged);
    };
  }, []);

  // Persist showBalance to localStorage
  useEffect(() => {
    localStorage.setItem("show-balance-statsbar", showBalance.toString());
  }, [showBalance]);

  // Persist showGross to localStorage
  useEffect(() => {
    localStorage.setItem("show-gross-statsbar", showGross.toString());
  }, [showGross]);
  
  // Check if cycling is allowed for percentage button
  const isCyclingAllowed = () => {
    let selectedYear = localStorage.getItem("year");
    try {
      selectedYear = JSON.parse(selectedYear);
    } catch (e) {
      // If not JSON, use as-is
    }
    selectedYear = String(selectedYear);
    const currentYear = String(new Date().getFullYear());
    
    return selectedYear === currentYear;
  };
  
  // Click animation helper
  const handleButtonClick = (buttonName, callback, isBlocked = false) => {
    const animationType = isBlocked ? `${buttonName}-blocked` : buttonName;
    setClickedButton(animationType);
    setTimeout(() => setClickedButton(null), 200); // Reset after 200ms
    if (callback && !isBlocked) callback();
  };
  
  // Listen for settings changes
  React.useEffect(() => {
    const handleSettingsChange = () => {
      const skipPreviousYears = localStorage.getItem("skipPreviousYears") === "true";
      const currentYear = String(new Date().getFullYear());
      const currentYearSelection = localStorage.getItem("year");
      
      // Reset year selection if it's no longer valid
      if (skipPreviousYears && currentYearSelection !== "0" && currentYearSelection !== currentYear) {
        localStorage.setItem("year", "0");
        setHeadingText("All");
      }
    };
    
    window.addEventListener('settings-changed', handleSettingsChange);
    return () => window.removeEventListener('settings-changed', handleSettingsChange);
  }, []);
  
  // Listen for year changes from external sources (e.g., opening a trade)
  React.useEffect(() => {
    const handleYearChange = (event) => {
      const year = event.detail?.year || localStorage.getItem("year");
      setHeadingText(year && year !== "0" ? year : "All");
    };
    
    window.addEventListener('year-changed', handleYearChange);
    return () => window.removeEventListener('year-changed', handleYearChange);
  }, []);
  
  // Use prop if provided, otherwise get from utils
  const selectedAccountId = propSelectedAccountId || getSelectedAccountId();
  
  // Auto-switch rotater to "All" when year changes away from current year
  React.useEffect(() => {
    const checkYearAndSetRotater = () => {
      const year = localStorage.getItem("year");
      let parsedYear = year;
      try {
        parsedYear = JSON.parse(year);
      } catch (e) {
        // If not JSON, use as-is
      }
      parsedYear = String(parsedYear);
      const currentYear = String(new Date().getFullYear());
      
      // If year is not current year, set rotater to "All" and trigger calculation
      if (parsedYear !== currentYear) {
        localStorage.setItem("rotater", "All");
        
        // Trigger calculation to update the display
        const tradesToCalculate = (getFilteredTradesByAccount && selectedAccountId)
          ? getFilteredTradesByAccount(auth.trades, selectedAccountId)
          : auth.trades;
        
        calculateFields(
          tradesToCalculate,
          auth.backtestingMode,
          localStorage.getItem("backtest-active"),
          "All"
        );
      }
    };
    
    // Check immediately
    checkYearAndSetRotater();
    
    // Poll for year changes every 500ms
    const interval = setInterval(checkYearAndSetRotater, 500);
    
    return () => clearInterval(interval);
  }, [selectedAccountId, calculateFields, getFilteredTradesByAccount, auth.trades, auth.backtestingMode]);
  
  // Load account data when selectedAccountId changes
  React.useEffect(() => {
    const loadAccount = async () => {
      if (selectedAccountId && selectedAccountId !== "All" && selectedAccountId !== "All Personal" && selectedAccountId !== "All Investor") {
        try {
          const account = await getAccountById(selectedAccountId);
          setSelectedAccount(account);
        } catch (error) {
          console.error("Error loading account:", error);
          setSelectedAccount(null);
        }
      } else {
        setSelectedAccount(null);
      }
    };
    
    loadAccount();
  }, [selectedAccountId]);
  
  // Get the visible trades - these are already filtered by year and account
  // Always use props from logic.js - these are updated by the rotater calculations
  const displayNetPercent = realisedPercentageWithFees || 0;
  const displayGrossPercent = realisedPercentage || 0;
  
  // Determine account type for currency detection
  const accountType = selectedAccount?.type || 
    (selectedAccountId === "All Personal" ? "Personal" :
     selectedAccountId === "All Investor" ? "Investor" :
     selectedAccountId === "All" ? "All" :
     selectedAccountId?.startsWith("pc-") ? "Personal" :
     selectedAccountId?.startsWith("ic-") ? "Investor" : null);
  
  // Get currency symbol
  const getCurrency = () => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    return getCurrencyForSelectedAccount(selectedAccountId, selectedAccount, user, false);
  };
  
  const currency = getCurrency();
  
  // Get balance values and starting balance from last trade
  const getBalanceValues = () => {
    const visibleTrades = auth.allTrades || [];
    const lastTrade = visibleTrades.length > 0 ? visibleTrades[visibleTrades.length - 1] : null;
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    
    if (!lastTrade) {
      return { grossBalance: 0, netBalance: 0, startingBalance: 0 };
    }
    
    if (accountType === "Personal" || selectedAccountId === "All Personal") {
      return {
        grossBalance: lastTrade.rollingBalancePCGross || 0,
        netBalance: lastTrade.rollingBalancePC || 0,
        startingBalance: parseFloat(user.startingBalancePC) || 0
      };
    } else if (accountType === "Investor" || selectedAccountId === "All Investor") {
      return {
        grossBalance: lastTrade.rollingBalanceICGross || 0,
        netBalance: lastTrade.rollingBalanceIC || 0,
        startingBalance: parseFloat(user.startingBalanceIC) || 0
      };
    } else if (selectedAccountId === "All") {
      const grossPC = lastTrade.rollingBalancePCGross || 0;
      const grossIC = lastTrade.rollingBalanceICGross || 0;
      const netPC = lastTrade.rollingBalancePC || 0;
      const netIC = lastTrade.rollingBalanceIC || 0;
      const startPC = parseFloat(user.startingBalancePC) || 0;
      const startIC = parseFloat(user.startingBalanceIC) || 0;
      return {
        grossBalance: grossPC + grossIC,
        netBalance: netPC + netIC,
        startingBalance: startPC + startIC
      };
    }
    
    return { grossBalance: 0, netBalance: 0, startingBalance: 0 };
  };
  
  const { grossBalance, netBalance, startingBalance } = getBalanceValues();
  
  // Color helper
  const getColor = (value, compareValue) => {
    if (value < compareValue) return "#f23645"; // Red
    if (value > compareValue) return "#26a65d"; // Green
    return "#ED9E0E"; // Orange
  };
  
  // Rotater for time periods - only works for current year or "All"
  const setRotater = async () => {
    // Debounce - prevent rapid clicks
    const now = Date.now();
    if (now - lastClickTime.current < 600) {
      return;
    }
    lastClickTime.current = now;
    
    let selectedYear = localStorage.getItem("year");
    // Handle JSON-stringified years
    try {
      selectedYear = JSON.parse(selectedYear);
    } catch (e) {
      // If not JSON, use as-is
    }
    selectedYear = String(selectedYear);
    const currentYear = String(new Date().getFullYear());
    
    // Only allow rotation for current year, lock to "All" for other years
    if (selectedYear !== currentYear) {
      // If not current year, set rotater to "All" and return
      localStorage.setItem("rotater", "All");
      return;
    }
    
    const storage = localStorage.getItem("rotater");
    
    // Filter trades by account before calculating (only if function is provided)
    const tradesToCalculate = (getFilteredTradesByAccount && selectedAccountId)
      ? getFilteredTradesByAccount(auth.trades, selectedAccountId)
      : auth.trades;
    
    if (storage === "Start Of Year") {
      calculateFields(
        tradesToCalculate,
        auth.backtestingMode,
        localStorage.getItem("backtest-active"),
        "3 Months"
      );
      localStorage.setItem("rotater", "3 Months");
    } else if (storage === "3 Months") {
      calculateFields(
        tradesToCalculate,
        auth.backtestingMode,
        localStorage.getItem("backtest-active"),
        "Monthly"
      );
      localStorage.setItem("rotater", "Monthly");
    } else if (storage === "Monthly") {
      calculateFields(
        tradesToCalculate,
        auth.backtestingMode,
        localStorage.getItem("backtest-active"),
        "Weekly"
      );
      localStorage.setItem("rotater", "Weekly");
    } else if (storage === "Weekly") {
      calculateFields(
        tradesToCalculate,
        auth.backtestingMode,
        localStorage.getItem("backtest-active"),
        "24 Hours"
      );
      localStorage.setItem("rotater", "24 Hours");
    } else if (storage === "24 Hours") {
      calculateFields(
        tradesToCalculate,
        auth.backtestingMode,
        localStorage.getItem("backtest-active"),
        "All"
      );
      localStorage.setItem("rotater", "All");
    } else {
      calculateFields(
        tradesToCalculate,
        auth.backtestingMode,
        localStorage.getItem("backtest-active"),
        "Start Of Year"
      );
      localStorage.setItem("rotater", "Start Of Year");
    }
  };
  
  // Year selector
  const [headingText, setHeadingText] = React.useState(() => {
    const storedYear = localStorage.getItem("year");
    return storedYear && storedYear !== "0" ? storedYear : "All";
  });
  
  const changeYear = async () => {
    const year = localStorage.getItem("year");
    const currentYear = String(new Date().getFullYear());
    const skipPreviousYears = localStorage.getItem("skipPreviousYears") === "true";
    
    let newYear;

    // Filter available years based on skipPreviousYears setting
    let availableYears = auth.allYearsOfTrades;
    if (skipPreviousYears) {
      availableYears = auth.allYearsOfTrades.filter(y => y === currentYear);
    }

    // If no available years (e.g., all years are filtered out), just cycle between current year and All
    if (availableYears.length === 0) {
      availableYears = [currentYear];
    }

    const availableFirst = availableYears[0];
    const availableLast = availableYears[availableYears.length - 1];

    if (year === availableFirst) {
      if (availableYears.length === 1 && availableFirst === currentYear) {
        // Only current year available, cycle to All
        newYear = "All";
        setHeadingText("All");
        localStorage.setItem("year", "0");
      } else {
        newYear = String(Number(availableFirst) + 1);
        setHeadingText(newYear);
        localStorage.setItem("year", JSON.stringify(Number(availableFirst) + 1));
      }
    } else if (year === availableLast) {
      newYear = "All";
      setHeadingText("All");
      localStorage.setItem("year", "0");
    } else if (year === "0") {
      newYear = availableFirst;
      setHeadingText(availableFirst);
      localStorage.setItem("year", availableFirst);
    } else {
      newYear = String(Number(year) + 1);
      setHeadingText(newYear);
      localStorage.setItem("year", JSON.stringify(Number(year) + 1));
    }

    // Set rotater to "All" for previous years, keep current for current year or "All" view
    if (newYear !== "All" && newYear !== currentYear) {
      localStorage.setItem("rotater", "All");
    }

    if (auth.backtestingMode) {
      showBacktestTrades({
        startDate: JSON.parse(localStorage.getItem("user")).created,
        archived: "false",
        notes: "",
        pair: "All missed trades from",
        testId: "Missed Trades",
      });
    } else {
      // Filter trades by account and recalculate
      const tradesToCalculate = (getFilteredTradesByAccount && selectedAccountId)
        ? getFilteredTradesByAccount(auth.trades, selectedAccountId)
        : auth.trades;
      
      // Don't pass rotater parameter so auth.setAllTrades gets called with filtered trades
      calculateFields(tradesToCalculate, auth.backtestingMode);
    }
  };
  
  const isDark = theme[0] === "dark";
  const currentRotater = localStorage.getItem("rotater") || "Start Of Year";
  
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      {/* Backtesting Mode Percentages */}
      {auth.backtestingMode && (
        <Tooltip title={`${currentRotater} - ${showGross ? 'Gross' : 'Net'} %`} arrow>
          <div
            style={{
              padding: '6px 12px',
              borderRadius: '8px',
              backgroundColor: clickedButton === 'percent-blocked' ? (isDark ? 'rgba(255, 100, 100, 0.2)' : 'rgba(255, 0, 0, 0.1)') : (isDark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.1)'),
              cursor: 'pointer',
              transition: 'all 0.2s',
              fontSize: '13px',
              fontWeight: 500,
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
              userSelect: 'none',
              transform: clickedButton === 'percent' ? 'scale(0.95)' : clickedButton === 'percent-blocked' ? 'scale(1.05)' : 'scale(1)',
              opacity: clickedButton === 'percent' ? 0.8 : clickedButton === 'percent-blocked' ? 0.6 : 1
            }}
            onClick={() => handleButtonClick('percent', setRotater, !isCyclingAllowed())}
          >
            <span style={{ color: getColor(showGross ? realisedPercentageBacktest : realisedPercentageWithFeesBacktest || 0, 0) }}>
              {Math.abs((showGross ? realisedPercentageBacktest : realisedPercentageWithFeesBacktest || 0).toFixed(2))}%
            </span>
          </div>
        </Tooltip>
      )}
      
      {/* Percentages - Respects statsbar gross/net setting */}
      {!auth.backtestingMode && (
        <Tooltip title={`${currentRotater} - ${showGross ? 'Gross' : 'Net'} %`} arrow>
          <div
            style={{
              padding: '6px 12px',
              borderRadius: '8px',
              backgroundColor: clickedButton === 'percent-blocked' ? (isDark ? 'rgba(255, 100, 100, 0.2)' : 'rgba(255, 0, 0, 0.1)') : (isDark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.1)'),
              cursor: 'pointer',
              transition: 'all 0.2s',
              fontSize: '13px',
              fontWeight: 500,
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
              userSelect: 'none',
              transform: clickedButton === 'percent' ? 'scale(0.95)' : clickedButton === 'percent-blocked' ? 'scale(1.05)' : 'scale(1)',
              opacity: clickedButton === 'percent' ? 0.8 : clickedButton === 'percent-blocked' ? 0.6 : 1
            }}
            onClick={() => handleButtonClick('percent', setRotater, !isCyclingAllowed())}
          >
          <span style={{ color: getColor(showGross ? displayGrossPercent : displayNetPercent, 0) }}>
            {Math.abs((showGross ? displayGrossPercent : displayNetPercent).toFixed(2))}%
          </span>
          </div>
        </Tooltip>
      )}

      {/* Year Selector */}
      <Tooltip title="Year" arrow>
        <div
        style={{
          padding: '6px 12px',
          borderRadius: '8px',
          backgroundColor: isDark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.1)',
          cursor: 'pointer',
          transition: 'all 0.2s',
          fontSize: '13px',
          fontWeight: 500,
          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
          color: isDark ? '#FFFFFF' : '#000000',
          userSelect: 'none',
          transform: clickedButton === 'year' ? 'scale(0.95)' : 'scale(1)',
          opacity: clickedButton === 'year' ? 0.8 : 1
        }}
        onClick={() => handleButtonClick('year', changeYear)}
      >
        {headingText !== "0" ? headingText : "All"}
        </div>
      </Tooltip>

      {/* Returns: P/L vs Balance (clickable toggle) */}
      {!auth.backtestingMode && (
        <Tooltip title={showBalance ? `${showGross ? 'Gross' : 'Net'} Balance` : `${showGross ? 'Gross' : 'Net'} P/L`} arrow>
          <div
            style={{
              padding: '6px 12px',
              borderRadius: '8px',
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.1)',
              cursor: 'pointer',
              transition: 'all 0.2s',
              fontSize: '13px',
              fontWeight: 500,
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
              userSelect: 'none',
              transform: clickedButton === 'balance' ? 'scale(0.95)' : 'scale(1)',
              opacity: clickedButton === 'balance' ? 0.8 : 1
            }}
            onClick={() => handleButtonClick('balance', () => setShowBalance(!showBalance))}
          >
          {showBalance ? (
            <span style={{ color: getColor(showGross ? grossBalance : netBalance, startingBalance) }}>
              {currency}{(showGross ? grossBalance : netBalance).toFixed(2)}
            </span>
          ) : (
            <span style={{ color: getColor(showGross ? realisedReturnGross : realisedReturn, 0) }}>
              {currency}{Math.abs(showGross ? realisedReturnGross : realisedReturn || 0).toFixed(2)}
            </span>
          )}
          </div>
        </Tooltip>
      )}
    </div>
  );
};

export default StatsBar;
